import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trash } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ScrollArea, ScrollBar } from '@/components/ui-kit/scroll-area';
import { useDeviceTableColumns } from '../devices-table-columns/devices-table-columns';
import { useSession } from '@/api-client/use-session';
import { Session } from '@/api-client/use-session/types';
import { useMe } from '@/api-client/use-me';
import { useLogOutAll } from '@/api-client/use-log-out-all';


/**
 * DevicesTable is a component that displays a paginated list of device sessions.
 * It fetches session data based on the current page, handles infinite scrolling,
 * and processes session data to be displayed in a table format.
 *
 */

export const Devices = () => {
  const { data: userInfo } = useMe();
  const [deviceSessions, setDeviceSessions] = useState<Session[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const loadingRef = useRef<HTMLDivElement>(null);
  const PAGE_SIZE = 10;
  const { t } = useTranslation();

  const { data: sessions, isLoading, refetch } = useSession(userInfo?.itemId);
  const columns = useDeviceTableColumns();
  const { logOutAllAsync, isLogOutAllPending } = useLogOutAll();

  const handleRemoveAllDevices = async () => {
    try {
      await logOutAllAsync();
      refetch();
    } catch (error) {
      console.error('Failed to log out all devices', error);
    }
  };

  useEffect(() => {
    if (sessions) {
      const processedSessions = sessions.filter((session: any): session is Session => session !== null);

      setDeviceSessions((prev) => {
        const newSessions = page === 0 ? processedSessions : [...prev, ...processedSessions];
        return newSessions;
      });
      setHasMore(processedSessions.length === PAGE_SIZE);
    } else {
      if (page === 0) {
        setDeviceSessions([]);
      }
      setHasMore(false);
    }
  }, [sessions, page]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !isLoading) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 1.0 }
    );

    const currentLoadingRef = loadingRef.current;
    if (currentLoadingRef) {
      observer.observe(currentLoadingRef);
    }

    return () => {
      if (currentLoadingRef) {
        observer.unobserve(currentLoadingRef);
      }
    };
  }, [hasMore, isLoading]);

  const table = useReactTable({
    data: deviceSessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      );
    }

    if (deviceSessions.length === 0) {
      return <p className="text-center">{t('NO_DEVICES_FOUND')}</p>;
    }

    return null;
  };

  return (
    <div className="flex">
      <Card className="w-full border-none rounded-xl shadow-sm">
        <CardHeader className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 pb-4">
          <div className="flex flex-col space-y-1">
            <CardTitle className="text-xl sm:text-2xl font-semibold tracking-tight text-high-emphasis">{t('MY_DEVICES')}</CardTitle>
            <CardDescription />
          </div>
          <Button
            size="sm"
            variant="ghost"
            disabled={isLogOutAllPending || deviceSessions.length === 0}
            onClick={handleRemoveAllDevices}
            className="group w-full sm:w-auto justify-center text-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-50 disabled:hover:bg-transparent transition-all duration-200"
          >
            {isLogOutAllPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash className="w-4 h-4 mr-2 disabled:text-gray-400 group-hover:scale-110 transition-transform" />
            )}
            <span className="text-sm font-semibold">
              {t('REMOVE_ALL_DEVICES')}
            </span>
          </Button>
        </CardHeader>
        <CardContent className="px-0 sm:px-6 sm:pb-6">
          <ScrollArea className="w-full rounded-md sm:border border-border/50">
            <Table className="text-sm">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="bg-muted/30 hover:bg-transparent">
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} className="font-semibold text-medium-emphasis py-4 px-4 h-auto">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {deviceSessions.length > 0 ? (
                  <>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer font-medium text-medium-emphasis hover:bg-muted/50 transition-colors"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <div ref={loadingRef} className="h-8 flex items-center justify-center">
                          {renderTableContent()}
                        </div>
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      {renderTableContent()}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};
